import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { WriteOnly, MaskedValue } from '../components/ViewerGuard';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../lib/api';
import api from '../lib/api';
import {
  Save, Loader2, Plus, X, Building2, Globe, Target,
  TrendingUp, Calendar, Award, CheckSquare, Square,
  ChevronDown, ChevronUp, Briefcase, DollarSign, History, RotateCcw
} from 'lucide-react';
import { SettingsNav } from './CognitiveProfilePage';
import usePageTitle from '../hooks/usePageTitle';

const inputCls = "w-full p-2.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-[#D4E2F0] text-sm focus:border-[#00D4C8] focus:ring-1 focus:ring-[#00D4C8]/20 placeholder:text-[#A3B5C7]/40";
const labelCls = "block text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-1 font-medium";

const SectionHeader = ({ icon: Icon, title, subtitle, collapsed, onToggle }) => (
  <button
    onClick={onToggle}
    className="w-full flex items-center justify-between py-3 group"
    data-testid={`section-toggle-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-[#00D4C8]/10 flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#00D4C8]" />
      </div>
      <div className="text-left">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {subtitle && <p className="text-[10px] text-[#A3B5C7]">{subtitle}</p>}
      </div>
    </div>
    {collapsed ? <ChevronDown className="w-4 h-4 text-[#A3B5C7]" /> : <ChevronUp className="w-4 h-4 text-[#A3B5C7]" />}
  </button>
);

const ListEditor = ({ items, onChange, placeholder }) => {
  const [newItem, setNewItem] = useState('');
  const addItem = () => {
    if (newItem.trim()) {
      onChange([...items, newItem.trim()]);
      setNewItem('');
    }
  };
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="flex-1 text-sm text-[#D4E2F0] bg-[#0A1628] px-3 py-1.5 rounded-lg border border-[#1A2F4C]">{item}</span>
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-red-400/50 hover:text-red-400 p-1">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <input
          className={inputCls}
          value={newItem}
          onChange={e => setNewItem(e.target.value)}
          placeholder={placeholder}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addItem())}
        />
        <button onClick={addItem} className="btn-secondary text-xs px-3 flex items-center gap-1" type="button">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
    </div>
  );
};

const RevenueStreamsEditor = ({ streams, onChange }) => {
  const [newLabel, setNewLabel] = useState('');
  const [newDetail, setNewDetail] = useState('');
  const addStream = () => {
    if (newLabel.trim()) {
      onChange([...streams, { label: newLabel.trim(), detail: newDetail.trim() }]);
      setNewLabel('');
      setNewDetail('');
    }
  };
  return (
    <div className="space-y-2">
      {streams.map((s, i) => (
        <div key={i} className="flex items-center gap-2 bg-[#0A1628] px-3 py-2 rounded-lg border border-[#1A2F4C]">
          <div className="flex-1 min-w-0">
            <span className="text-sm text-[#D4E2F0] font-medium">{s.label}</span>
            {s.detail && <span className="text-xs text-[#A3B5C7] ml-2">— {s.detail}</span>}
          </div>
          <button onClick={() => onChange(streams.filter((_, j) => j !== i))} className="text-red-400/50 hover:text-red-400 p-1 flex-shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
        <input className={inputCls} value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Revenue stream name" />
        <input className={inputCls} value={newDetail} onChange={e => setNewDetail(e.target.value)} placeholder="Details (optional)"
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addStream())} />
        <button onClick={addStream} className="btn-secondary text-xs px-3 flex items-center gap-1" type="button">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
    </div>
  );
};

const KeyEventsEditor = ({ events, onChange }) => {
  const [newName, setNewName] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const addEvent = () => {
    if (newName.trim()) {
      onChange([...events, { name: newName.trim(), date: newDate.trim(), notes: newNotes.trim() }]);
      setNewName(''); setNewDate(''); setNewNotes('');
    }
  };
  return (
    <div className="space-y-2">
      {events.map((e, i) => (
        <div key={i} className="flex items-center gap-2 bg-[#0A1628] px-3 py-2 rounded-lg border border-[#1A2F4C]">
          <div className="flex-1 min-w-0">
            <span className="text-sm text-white font-medium">{e.name}</span>
            {e.date && <span className="text-xs text-[#00D4C8] ml-2">{e.date}</span>}
            {e.notes && <span className="text-xs text-[#A3B5C7] ml-2">— {e.notes}</span>}
          </div>
          <button onClick={() => onChange(events.filter((_, j) => j !== i))} className="text-red-400/50 hover:text-red-400 p-1 flex-shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-2">
        <input className={inputCls} value={newName} onChange={e => setNewName(e.target.value)} placeholder="Event name" />
        <input className={inputCls} value={newDate} onChange={e => setNewDate(e.target.value)} placeholder="Date / period" />
        <input className={inputCls} value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="Notes"
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addEvent())} />
        <button onClick={addEvent} className="btn-secondary text-xs px-3 flex items-center gap-1" type="button">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
    </div>
  );
};

const OPPORTUNITY_OPTIONS = [
  "Foundation / philanthropic grants",
  "Government / public innovation funding",
  "R&D / product development grants",
  "Accelerators & incubators",
  "Competitions, challenge funds & prizes",
  "Impact investment (equity)",
  "Angel / seed / venture investment",
  "Corporate partnerships & sponsorship",
  "Brand partnerships & integration",
  "Distribution & channel partnerships",
  "Licensing & royalties revenue",
  "Subscription / recurring-revenue clients",
  "Consulting / advisory retainers",
  "Commissioned work & contracts",
  "Awards & recognition",
];

const OrgProfilePage = () => {
  usePageTitle('Settings');
  const { isAdmin, isTester } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [versions, setVersions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const toggle = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const loadVersions = useCallback(async () => {
    try {
      const res = await api.get('/org-profile/versions');
      setVersions(res.data.versions || []);
    } catch (err) {
      console.error('Failed to load org profile versions:', err);
    }
  }, []);

  const handleRestore = async (versionId) => {
    setRestoringId(versionId);
    try {
      const res = await api.post(`/org-profile/versions/${versionId}/restore`);
      setProfile(res.data.profile);
      toast.success('Organisation profile restored');
      await loadVersions();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRestoringId(null);
    }
  };

  const loadProfile = useCallback(async () => {
    try {
      const res = await api.get('/org-profile');
      if (res.data && res.data.exists !== false) {
        setProfile(res.data);
      } else {
        setProfile({});
      }
    } catch (err) {
      console.error('Failed to load org profile:', err);
      setProfile({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/org-profile', profile);
      setProfile(res.data);
      toast.success('Organisation profile saved');
      if (isAdmin && showHistory) await loadVersions();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => setProfile(prev => ({ ...prev, [field]: value }));

  const toggleOpp = (opp) => {
    const current = profile.opportunity_universe || [];
    if (current.includes(opp)) {
      update('opportunity_universe', current.filter(o => o !== opp));
    } else {
      update('opportunity_universe', [...current, opp]);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-[#00D4C8]" />
    </div>
  );

  // 2G — Settings restricted to admins/viewers; testers get no access.
  if (isTester) return (
    <div className="max-w-2xl mx-auto mt-16 card-surface p-8 text-center" data-testid="settings-restricted">
      <h2 className="font-heading text-2xl text-white mb-2">Settings are restricted</h2>
      <p className="text-sm text-[#A3B5C7]">Organisation profile and thinking model are managed by administrators. Contact an admin if a change is needed.</p>
    </div>
  );

  return (
    <div className="max-w-4xl space-y-6" data-testid="org-profile-page">
      <SettingsNav />
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white tracking-tight">Organisation Profile</h1>
          <p className="text-sm text-[#A3B5C7] mt-1">
            Master intelligence layer — drives every AI output across BREACH
          </p>
        </div>
        <WriteOnly>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary text-sm flex items-center gap-2"
          data-testid="save-org-profile-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Profile
        </button>
        </WriteOnly>
      </div>

      {profile?.updated_at && (
        <p className="text-[10px] text-[#A3B5C7]/60">
          Last updated: {new Date(profile.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          {profile.updated_by && ` by ${profile.updated_by}`}
        </p>
      )}

      {/* 2H — Version history (admin only). Every save snapshots the previous
          content; one-click restore reverts the synthesis identity. */}
      {isAdmin && (
        <div className="card-surface p-4" data-testid="org-version-history">
          <button
            onClick={() => { const next = !showHistory; setShowHistory(next); if (next) loadVersions(); }}
            className="flex items-center gap-2 text-sm text-[#A3B5C7] hover:text-white transition-colors"
            data-testid="org-version-history-toggle"
          >
            <History className="w-4 h-4" />
            Version history
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showHistory && (
            <div className="mt-4 space-y-2">
              {versions.length === 0 ? (
                <p className="text-xs text-[#A3B5C7]/60">No previous versions yet. Each save records one.</p>
              ) : versions.map((v) => (
                <div key={v.id} className="flex items-center justify-between p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]" data-testid={`org-version-${v.id}`}>
                  <div className="text-xs text-[#D4E2F0]">
                    <span className="text-[#A3B5C7]">{new Date(v.edited_at).toLocaleString('en-GB')}</span>
                    <span className="mx-2 text-[#A3B5C7]/40">·</span>
                    {v.edited_by || 'Unknown'}
                    {v.action === 'pre-restore-snapshot' && <span className="ml-2 text-amber-400/80">(auto-snapshot)</span>}
                    <div className="text-[#A3B5C7]/70 mt-1">org: {v.content?.org_name || '—'} · tagline: {(v.content?.tagline || '—').slice(0, 60)}</div>
                  </div>
                  <button
                    onClick={() => handleRestore(v.id)}
                    disabled={restoringId === v.id}
                    className="btn-secondary text-xs flex items-center gap-1.5 shrink-0"
                    data-testid={`org-version-restore-${v.id}`}
                  >
                    {restoringId === v.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                    Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Section 1: Business Identity */}
      <motion.div layout className="card-surface overflow-hidden">
        <div className="px-5 border-b border-[#1A2F4C]">
          <SectionHeader icon={Building2} title="Business Identity" subtitle="Who you are, where you operate" collapsed={collapsed.identity} onToggle={() => toggle('identity')} />
        </div>
        {!collapsed.identity && (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div><label className={labelCls}>Organisation Name</label><input className={inputCls} value={profile.org_name || ''} onChange={e => update('org_name', e.target.value)} data-testid="org-name-input" /></div>
              <div><label className={labelCls}>Type</label><input className={inputCls} value={profile.org_type || ''} onChange={e => update('org_type', e.target.value)} placeholder="e.g. your organisation type" /></div>
              <div><label className={labelCls}>Legal Structure</label><input className={inputCls} value={profile.legal_structure || ''} onChange={e => update('legal_structure', e.target.value)} placeholder="e.g. UK-registered" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><label className={labelCls}>HQ</label><input className={inputCls} value={profile.hq || ''} onChange={e => update('hq', e.target.value)} /></div>
              <div><label className={labelCls}>Operating Territories</label><input className={inputCls} value={profile.operating_territories || ''} onChange={e => update('operating_territories', e.target.value)} /></div>
              <div><label className={labelCls}>Eligible Funding Jurisdictions</label><input className={inputCls} value={profile.eligible_jurisdictions || ''} onChange={e => update('eligible_jurisdictions', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Stage</label>
                <select className={inputCls} value={profile.stage || ''} onChange={e => update('stage', e.target.value)} data-testid="org-stage-select">
                  <option value="">Select...</option>
                  <option value="Pre-revenue">Pre-revenue</option>
                  <option value="Early-stage, pre-scale">Early-stage, pre-scale</option>
                  <option value="Growth">Growth</option>
                  <option value="Established">Established</option>
                </select>
              </div>
              <div className="col-span-2"><label className={labelCls}>Tagline</label><input className={inputCls} value={profile.tagline || ''} onChange={e => update('tagline', e.target.value)} /></div>
            </div>
            <div><label className={labelCls}>Mission Statement</label><textarea className={inputCls + " h-20"} value={profile.mission || ''} onChange={e => update('mission', e.target.value)} data-testid="org-mission-input" /></div>
            <div>
              <label className={labelCls}>Sector Tags</label>
              <ListEditor items={profile.sector_tags || []} onChange={v => update('sector_tags', v)} placeholder="e.g. your sector tags" />
            </div>
            <div>
              <label className={labelCls}>Event Search Terms <span className="text-[#A3B5C7]/60 font-normal">(optional — verbatim event anchors)</span></label>
              <ListEditor items={profile.event_search_terms || []} onChange={v => update('event_search_terms', v)} placeholder='e.g. COP31 Antalya, Climate Week NYC' />
              <p className="text-[10px] text-[#A3B5C7]/60 mt-1" data-testid="event-search-terms-hint">Exact, high-signal event names/acronyms searched verbatim (e.g. "COP31 Antalya", "Climate Week NYC", "UN climate conference") — added on top of the auto themes derived from Sector Tags. Use these to guarantee specific marquee events surface in the Events scan.</p>
            </div>
          </div>
        )}
      </motion.div>

      {/* Section 2: Business Model */}
      <motion.div layout className="card-surface overflow-hidden">
        <div className="px-5 border-b border-[#1A2F4C]">
          <SectionHeader icon={DollarSign} title="Business Model" subtitle="Revenue, funding cycle, income split" collapsed={collapsed.model} onToggle={() => toggle('model')} />
        </div>
        {!collapsed.model && (
          <div className="p-5 space-y-4">
            <div>
              <label className={labelCls}>Revenue Streams</label>
              <RevenueStreamsEditor streams={profile.revenue_streams || []} onChange={v => update('revenue_streams', v)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Current Income Split</label><textarea className={inputCls + " h-16"} value={profile.income_split_current || ''} onChange={e => update('income_split_current', e.target.value)} /></div>
              <div><label className={labelCls}>Target Income Split</label><textarea className={inputCls + " h-16"} value={profile.income_split_target || ''} onChange={e => update('income_split_target', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Funding Cycle</label><input className={inputCls} value={profile.funding_cycle || ''} onChange={e => update('funding_cycle', e.target.value)} /></div>
              <div><label className={labelCls}>Primary Funders / Clients</label><input className={inputCls} value={profile.primary_funders || ''} onChange={e => update('primary_funders', e.target.value)} /></div>
            </div>
            <div><label className={labelCls}>12-Month Goal</label><textarea className={inputCls + " h-16"} value={profile.twelve_month_goal || ''} onChange={e => update('twelve_month_goal', e.target.value)} data-testid="org-12month-goal" /></div>
          </div>
        )}
      </motion.div>

      {/* Section 3: BD Streams */}
      <motion.div layout className="card-surface overflow-hidden">
        <div className="px-5 border-b border-[#1A2F4C]">
          <SectionHeader icon={Briefcase} title="BD Streams" subtitle="Activities, audiences, deal size, key events" collapsed={collapsed.bd} onToggle={() => toggle('bd')} />
        </div>
        {!collapsed.bd && (
          <div className="p-5 space-y-4">
            <div>
              <label className={labelCls}>Primary BD Activities</label>
              <ListEditor items={profile.bd_activities || []} onChange={v => update('bd_activities', v)} placeholder="e.g. Investor fundraising" />
            </div>
            <div>
              <label className={labelCls}>Target Audiences</label>
              <ListEditor items={profile.target_audiences || []} onChange={v => update('target_audiences', v)} placeholder="e.g. funders, investors, commissioners" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Typical Deal Size</label><input className={inputCls} value={profile.typical_deal_size || ''} onChange={e => update('typical_deal_size', e.target.value)} /></div>
              <div><label className={labelCls}>Sales Cycle Length</label><input className={inputCls} value={profile.sales_cycle_length || ''} onChange={e => update('sales_cycle_length', e.target.value)} /></div>
            </div>
            <div>
              <label className={labelCls}>Key Events</label>
              <KeyEventsEditor events={profile.key_events || []} onChange={v => update('key_events', v)} />
            </div>
          </div>
        )}
      </motion.div>

      {/* Section 4: Credentials */}
      <motion.div layout className="card-surface overflow-hidden">
        <div className="px-5 border-b border-[#1A2F4C]">
          <SectionHeader icon={Award} title="Credentials & Milestones" subtitle="Achievements injected into outreach and briefs" collapsed={collapsed.creds} onToggle={() => toggle('creds')} />
        </div>
        {!collapsed.creds && (
          <div className="p-5">
            <ListEditor items={profile.credentials || []} onChange={v => update('credentials', v)} placeholder="e.g. an award or milestone" />
          </div>
        )}
      </motion.div>

      {/* Section 5: Opportunity Universe */}
      <motion.div layout className="card-surface overflow-hidden">
        <div className="px-5 border-b border-[#1A2F4C]">
          <SectionHeader icon={Globe} title="Opportunity Universe" subtitle="Controls which opportunity types BREACH surfaces in funding scans" collapsed={collapsed.opp} onToggle={() => toggle('opp')} />
        </div>
        {!collapsed.opp && (
          <div className="p-5">
            <p className="text-xs text-[#A3B5C7] mb-4">
              Only ticked categories will be searched during Funding Intelligence Scans. Untick categories irrelevant to your business.
            </p>
            <div className="grid grid-cols-2 gap-2" data-testid="opportunity-universe-grid">
              {OPPORTUNITY_OPTIONS.map(opp => {
                const active = (profile.opportunity_universe || []).includes(opp);
                return (
                  <button
                    key={opp}
                    onClick={() => toggleOpp(opp)}
                    className={`flex items-center gap-3 p-3 rounded-lg border text-left text-sm transition-all ${
                      active
                        ? 'bg-[#00D4C8]/5 border-[#00D4C8]/25 text-[#D4E2F0]'
                        : 'bg-[#0A1628]/50 border-[#1A2F4C] text-[#A3B5C7]/50'
                    }`}
                    data-testid={`opp-toggle-${opp.substring(0, 20).toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  >
                    {active ? <CheckSquare className="w-4 h-4 text-[#00D4C8] flex-shrink-0" /> : <Square className="w-4 h-4 text-[#A3B5C7]/30 flex-shrink-0" />}
                    {opp}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-[#A3B5C7]/50 mt-3">
              {(profile.opportunity_universe || []).length} / {OPPORTUNITY_OPTIONS.length} categories active
            </p>
          </div>
        )}
      </motion.div>

      {/* Bottom save */}
      <WriteOnly>
      <div className="flex justify-end pb-8">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary text-sm flex items-center gap-2"
          data-testid="save-org-profile-btn-bottom"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Profile
        </button>
      </div>
      </WriteOnly>
    </div>
  );
};

export default OrgProfilePage;
